import { useState } from 'react';
import { Plus, Pencil, XCircle } from 'lucide-react';
import { Button, Badge, Modal, Input, EmptyState, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useCompanies,
  useCreateCompany,
  useUpdateCompany,
  useDeactivateCompany,
} from '../../../hooks/useFinancialOps';

const COMPANY_TYPE_LABELS: Record<string, string> = {
  MEI: 'MEI',
  LTDA: 'LTDA',
  SA: 'S/A',
  EIRELI: 'EIRELI',
  SLU: 'SLU',
  OTHER: 'Outro',
};

const TAX_REGIME_LABELS: Record<string, string> = {
  SIMPLES_NACIONAL: 'Simples Nacional',
  LUCRO_PRESUMIDO: 'Lucro Presumido',
  LUCRO_REAL: 'Lucro Real',
  MEI: 'MEI',
};

interface CompanyFormData {
  name: string;
  tradeName: string;
  cnpj: string;
  email: string;
  phone: string;
  companyType: string;
  taxRegime: string;
  defaultTaxRatePct: string;
  notes: string;
}

const emptyForm: CompanyFormData = {
  name: '',
  tradeName: '',
  cnpj: '',
  email: '',
  phone: '',
  companyType: 'LTDA',
  taxRegime: 'SIMPLES_NACIONAL',
  defaultTaxRatePct: '',
  notes: '',
};

const DEFAULT_TAX_RATE_HINT: Record<string, string> = {
  SIMPLES_NACIONAL: '6,00 %',
  LUCRO_PRESUMIDO: '11,33 %',
  LUCRO_REAL: '0,00 % (depende do lucro)',
  MEI: '0,00 % (DAS é fixo)',
};

export function EmpresasPage() {
  const { data, isLoading } = useCompanies();
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const deactivateCompany = useDeactivateCompany();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CompanyFormData>(emptyForm);

  const companies = data?.data ?? [];

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  }

  function openEdit(company: any) {
    setEditingId(company.id);
    setForm({
      name: company.name ?? '',
      tradeName: company.tradeName ?? '',
      cnpj: company.cnpj ?? '',
      email: company.email ?? '',
      phone: company.phone ?? '',
      companyType: company.companyType ?? 'LTDA',
      taxRegime: company.taxRegime ?? 'SIMPLES_NACIONAL',
      defaultTaxRatePct:
        company.defaultTaxRate !== null && company.defaultTaxRate !== undefined
          ? String(Number(company.defaultTaxRate) * 100)
          : '',
      notes: company.notes ?? '',
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const { defaultTaxRatePct, ...rest } = form;
    const payload: Record<string, any> = { ...rest };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '') {
        delete payload[k];
      }
    });
    if (defaultTaxRatePct !== '') {
      const pct = parseFloat(defaultTaxRatePct);
      if (!isNaN(pct)) {
        payload.defaultTaxRate = pct / 100;
      }
    } else if (editingId) {
      payload.defaultTaxRate = null;
    }
    try {
      if (editingId) {
        await updateCompany.mutateAsync({ id: editingId, data: payload });
        toast.success('Empresa atualizada');
      } else {
        await createCompany.mutateAsync(payload);
        toast.success('Empresa criada');
      }
      setShowModal(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.details?.[0]?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Erro ao salvar empresa';
      toast.error(msg);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-ink">Empresas</h2>
        <Button onClick={openCreate} size="sm">
          <Plus size={16} className="mr-1" /> Nova Empresa
        </Button>
      </div>

      {companies.length === 0 ? (
        <EmptyState
          title="Nenhuma empresa cadastrada"
          body="Clique em Nova Empresa para começar."
        />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Nome</th>
                <th className="px-4 py-2 text-left font-medium">CNPJ</th>
                <th className="px-4 py-2 text-left font-medium">Tipo</th>
                <th className="px-4 py-2 text-left font-medium">Regime</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company: any) => (
                <tr key={company.id} className="border-t border-border hover:bg-subtle/50">
                  <td className="px-4 py-3 font-medium text-ink">{company.name}</td>
                  <td className="px-4 py-3 text-ink-secondary">{company.cnpj ?? '—'}</td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {COMPANY_TYPE_LABELS[company.companyType] ?? company.companyType}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {TAX_REGIME_LABELS[company.taxRegime] ?? company.taxRegime}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={company.active ? 'accent' : 'neutral'}>
                      {company.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(company)}
                        className="text-ink-secondary hover:text-ink-primary"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      {company.active && (
                        <button
                          onClick={() => deactivateCompany.mutate(company.id)}
                          className="text-ink-secondary hover:text-red-500"
                          title="Desativar"
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Empresa' : 'Nova Empresa'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="Nome Fantasia"
            value={form.tradeName}
            onChange={(e) => setForm((f) => ({ ...f, tradeName: e.target.value }))}
          />
          <Input
            label="CNPJ"
            value={form.cnpj}
            onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value }))}
            placeholder="00.000.000/0000-00"
          />
          <Input
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="Telefone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
                Tipo
              </label>
              <select
                className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
                value={form.companyType}
                onChange={(e) => setForm((f) => ({ ...f, companyType: e.target.value }))}
              >
                {Object.entries(COMPANY_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
                Regime Tributário
              </label>
              <select
                className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
                value={form.taxRegime}
                onChange={(e) => setForm((f) => ({ ...f, taxRegime: e.target.value }))}
              >
                {Object.entries(TAX_REGIME_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Input
              label="Alíquota de imposto sobre faturamento (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.defaultTaxRatePct}
              onChange={(e) => setForm((f) => ({ ...f, defaultTaxRatePct: e.target.value }))}
              placeholder={DEFAULT_TAX_RATE_HINT[form.taxRegime] ?? ''}
            />
            <p className="text-xs text-ink-tertiary mt-1">
              Em branco usa o padrão do regime ({DEFAULT_TAX_RATE_HINT[form.taxRegime] ?? '—'}).
              Aplicado na receita bruta de cada repasse Stripe ao confirmar.
            </p>
          </div>
          <Input
            label="Observações"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={createCompany.isPending || updateCompany.isPending}>
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
